import { SocketV2State } from './stateMachine';
import { ReconnectInfo, RetryConfig, RetryTracker, getCooldownMs, getRetryDelayMs } from './retryPolicy';
import { SubscribableSocketV2Event } from './types';

/** The slice of the socket's event union this controller is allowed to emit. */
export type ReconnectionEvent = Extract<
  SubscribableSocketV2Event<never>,
  { type: 'disconnected' | 'failed' | 'reconnecting' | 'resumed' | 'suspended' }
>;

/**
 * Everything the controller needs from the socket. It never touches the transport, the
 * heartbeat or the queue directly — the socket stays the only owner of those.
 */
export interface ReconnectionContext {
  getState: () => SocketV2State;
  transition: (to: SocketV2State) => boolean;
  dispatch: (event: ReconnectionEvent) => void;
  /** Fire a connection attempt (the socket's connectAttempt). */
  connect: () => void;
  isOnline: () => boolean;
  /** Stats clock hook: an outage just began. */
  onOutageStarted: () => void;
  /** Server-mandated retry delay extracted from a close event, when configured. */
  retryAfterFromClose?: (event: CloseEvent) => number | undefined;
  isFatalCloseCode: (code: number) => boolean;
  log: (level: 'debug' | 'info' | 'warn' | 'error', event: string, meta?: Record<string, unknown>) => void;
}

/**
 * Owns the whole reconnection policy: the retry ladder, the single armed timer, and the two
 * flags that colour a reopen ('reconnected' vs 'open') and an exit from 'suspended'
 * (offline resume vs cooldown expiry). Nothing outside this file may read or write them.
 */
export class ReconnectionController {
  private tracker: RetryTracker;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private wasOutage = false; // reopen should emit 'reconnected' instead of 'open'
  private suspendReason?: 'offline' | 'cooldown'; // disambiguates 'suspended' so online/wake only resume the offline case, never a flap cooldown

  constructor(
    private retryConfig: RetryConfig,
    private ctx: ReconnectionContext,
  ) {
    this.tracker = new RetryTracker(retryConfig);
  }

  /**
   * The transport is gone. Caller has already stopped the heartbeat; from here the policy
   * decides between failing, suspending and scheduling the next attempt.
   */
  public handleDrop = (closeEvent: CloseEvent | undefined): void => {
    this.ctx.onOutageStarted();
    this.tracker.onDropped();
    this.wasOutage = true;

    // Fatal close code → give up loudly.
    if (closeEvent !== undefined) {
      if (this.ctx.isFatalCloseCode(closeEvent.code)) {
        this.ctx.log('warn', 'fatal-close-code', { code: closeEvent.code });
        this.clearReconnectTimer();
        this.ctx.transition('failed');
        this.ctx.dispatch({ event: closeEvent, reason: 'fatal-close-code', type: 'failed' });
        return;
      }
    }

    const info = this.tracker.info();
    this.ctx.dispatch({ errorEvent: closeEvent, info, type: 'disconnected' });

    // Offline → suspend; NetworkMonitor's onOnline resumes (Task 8). Any armed backoff or
    // cooldown timer must die here: nothing may fire while we are deliberately not retrying.
    if (!this.ctx.isOnline()) {
      this.clearReconnectTimer();
      // The reason must become 'offline' even when we are ALREADY suspended (e.g. reconnect()
      // called during a flap cooldown while offline): the cooldown timer that used to own the
      // exit was just cleared on the line above, so network resume is the only way out now —
      // leaving the reason as 'cooldown' would make every resume path refuse and strand us.
      this.suspendReason = 'offline';
      // 'suspended' -> 'suspended' is not a legal transition, and there is nothing new to
      // announce when we are already parked there.
      if (this.ctx.getState() !== 'suspended') {
        this.ctx.transition('suspended');
        this.ctx.dispatch({ reason: 'offline', type: 'suspended' });
      }
      return;
    }

    // Flap cooldown → suspend on a timer.
    if (this.tracker.shouldCooldown()) {
      this.enterCooldown();
      return;
    }

    this.scheduleReconnect(closeEvent);
  };

  /**
   * Handshake completed. Tells the caller whether this reopen closes an outage (→ 'reconnected')
   * or is a plain first connect (→ 'open'), along with the info payload to announce.
   */
  public onConnected = (): { reconnected: boolean; info: ReconnectInfo } => {
    this.tracker.onOpened();
    const reconnected = this.wasOutage;
    this.wasOutage = false;
    return { info: this.tracker.info(), reconnected };
  };

  /**
   * Network came back. Returns false (and does nothing) unless we are parked in 'suspended'
   * for the offline reason — a flap cooldown's timer owns its own exit.
   */
  public resumeFromOffline = (): boolean => {
    // Authoritative copy of the suspended-offline predicate: socket.ts's handleOnline pre-checks
    // the same condition via canResumeFromOffline() so it can skip its 'online' log when nothing
    // would resume — the two must stay in sync.
    if (this.ctx.getState() !== 'suspended' || this.suspendReason !== 'offline') {
      return false;
    }
    this.suspendReason = undefined;
    this.clearReconnectTimer();
    this.ctx.dispatch({ type: 'resumed' });
    this.ctx.transition('reconnecting');
    this.ctx.dispatch({ info: this.tracker.info(), type: 'reconnecting' });
    this.tracker.nextAttempt();
    this.ctx.connect(); // single immediate attempt; failure re-enters normal backoff
    return true;
  };

  /** Park in 'suspended' because the network went away. Caller has already torn the transport down. */
  public suspendForOffline = (): void => {
    this.wasOutage = true;
    this.suspendReason = 'offline';
    this.ctx.transition('suspended');
    this.ctx.dispatch({ reason: 'offline', type: 'suspended' });
  };

  /** True when a 'suspended' state is the offline kind, i.e. an online/wake signal may resume it. */
  public canResumeFromOffline = (): boolean => this.suspendReason === 'offline';

  /**
   * Offline arrived while already parked (typically a flap cooldown). Convert the
   * reason so a later `online` can resume immediately instead of waiting out the
   * cooldown timer — that timer would otherwise ignore the network coming back.
   */
  public markSuspendedOffline = (): void => {
    this.suspendReason = 'offline';
    this.clearReconnectTimer();
  };

  /** Disarm whatever is scheduled. The only way anyone outside can touch the timer. */
  public cancel = (): void => {
    this.clearReconnectTimer();
  };

  /** Back to a virgin ladder: used by open() and close(). */
  public reset = (): void => {
    this.clearReconnectTimer();
    this.tracker.reset();
    this.wasOutage = false;
    this.suspendReason = undefined;
  };

  private scheduleReconnect = (closeEvent?: CloseEvent): void => {
    this.clearReconnectTimer();
    if (this.tracker.isExhausted()) {
      this.ctx.transition('failed');
      this.ctx.dispatch({ event: closeEvent, reason: 'max-retries', type: 'failed' });
      return;
    }
    this.ctx.transition('reconnecting');
    this.ctx.dispatch({ errorEvent: closeEvent, info: this.tracker.info(), type: 'reconnecting' });
    const hint = closeEvent !== undefined ? this.ctx.retryAfterFromClose?.(closeEvent) : undefined;
    const attempt = this.tracker.nextAttempt();
    if (this.tracker.isExhausted()) {
      this.ctx.transition('failed');
      this.ctx.dispatch({ event: closeEvent, reason: 'max-retries', type: 'failed' });
      return;
    }
    const delay = getRetryDelayMs(attempt, this.retryConfig, hint);
    this.ctx.log('info', 'reconnect-scheduled', { attempt, delayMs: Math.round(delay) });
    if (delay <= 0) {
      this.ctx.connect();
    } else {
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = undefined;
        if (this.ctx.getState() === 'reconnecting') {
          this.ctx.connect();
        }
      }, delay);
    }
  };

  private enterCooldown = (): void => {
    this.clearReconnectTimer();
    this.suspendReason = 'cooldown';
    this.ctx.transition('suspended');
    const cooldown = getCooldownMs(this.retryConfig);
    this.ctx.log('warn', 'cooldown', { cooldownMs: Math.round(cooldown) });
    this.ctx.dispatch({ reason: 'cooldown', type: 'suspended' });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      if (this.ctx.getState() !== 'suspended') return;
      if (!this.ctx.isOnline()) {
        // The cooldown outlived the network. Connecting now would burn an attempt on a
        // guaranteed failure, so stay suspended and hand the exit to the online signal —
        // nothing new to announce, we are already parked in 'suspended'.
        this.suspendReason = 'offline';
        this.ctx.log('info', 'cooldown-expired-offline');
        return;
      }
      this.suspendReason = undefined;
      this.ctx.dispatch({ type: 'resumed' });
      this.ctx.transition('reconnecting');
      this.ctx.dispatch({ info: this.tracker.info(), type: 'reconnecting' });
      this.tracker.nextAttempt();
      this.ctx.connect();
    }, cooldown);
  };

  private clearReconnectTimer = (): void => {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
  };
}
